import { useCallback, useEffect, useState } from 'react';

import {
  loadServerPreferences,
  saveServerPreferences,
  type PreferenceSync,
} from './preferences-api';

export type AppTheme = 'light' | 'dark';

const THEME_STORAGE_PREFIX = 'quality.vet:theme:';

function isAppTheme(value: string | null): value is AppTheme {
  return value === 'light' || value === 'dark';
}

function preferenceKey(userKey: string): string {
  return `${THEME_STORAGE_PREFIX}${encodeURIComponent(userKey)}`;
}

export function useThemePreference(userKey: string | undefined, sync?: PreferenceSync) {
  const [theme, setThemeState] = useState<AppTheme>('light');

  useEffect(() => {
    if (!userKey) {
      setThemeState('light');
      return;
    }

    try {
      const savedTheme = window.localStorage.getItem(preferenceKey(userKey));
      setThemeState(isAppTheme(savedTheme) ? savedTheme : 'light');
    } catch {
      setThemeState('light');
    }
  }, [userKey]);

  useEffect(() => {
    if (!userKey || !sync?.accessToken) return;
    let active = true;
    void loadServerPreferences(sync).then((saved) => {
      if (!active || !saved) return;
      setThemeState(saved.theme);
      try {
        window.localStorage.setItem(preferenceKey(userKey), saved.theme);
      } catch {
        // Server preferences remain authoritative when browser storage is unavailable.
      }
    });
    return () => {
      active = false;
    };
  }, [sync?.accessToken, sync?.apiUrl, userKey]);

  const setTheme = useCallback(
    (nextTheme: AppTheme) => {
      setThemeState(nextTheme);
      if (!userKey) return;

      try {
        window.localStorage.setItem(preferenceKey(userKey), nextTheme);
      } catch {
        // The in-memory preference still applies when storage is unavailable.
      }
      if (sync) void saveServerPreferences(sync, { theme: nextTheme }).catch(() => undefined);
    },
    [sync, userKey],
  );

  return { theme, setTheme };
}
